import React from 'react';

import {
  ConversationForAttendeeQueryResult,
  ConversationQueryResult,
  useConversationForAttendeeLazyQuery,
  useConversationLazyQuery,
} from '$/GraphQL/schema/schema';
import {
  AttendeeFragment,
  ConversationForAttendeeQuery,
  ConversationFragment,
  ConversationQuery,
  ConversationWithAttendeePropertiesFragment,
  MessageFragment,
} from '$/GraphQL/schema/types';
import { logger } from '$/utils/logger';

export type ConversationWithMessages = ConversationFragment & { messages: MessageFragment[] };
export type ConversationWithMessage = (
  | ConversationFragment
  | ConversationWithAttendeePropertiesFragment
) & { messages: MessageFragment[] };
type QueryResult = ConversationForAttendeeQueryResult | ConversationQueryResult;

export interface GetConversationHook {
  loading: boolean;
  called: boolean;
  conversation?: ConversationWithMessages;
  conversationQueryResult?: QueryResult;
  moderatorConversationQueryResult?: ConversationQueryResult;
}

export const useGetConversation = (
  attendee?: AttendeeFragment,
  conversationId?: string
): GetConversationHook => {
  const [loading, setLoading] = React.useState(false);
  const [called, setCalled] = React.useState(false);
  const [conversation, setConversation] = React.useState<ConversationWithMessages>();
  const [conversationQueryResult, setConversationQueryResult] = React.useState<QueryResult>();

  const attendeeId = attendee?.id;

  /**
   * @todo simplify using a single query since we always have a Conversation
   */
  const [getAttendeeConversationQuery, getAttendeeConversationResponse] =
    useConversationForAttendeeLazyQuery({
      fetchPolicy: 'network-only',
    });
  const [getModeratorConversationQuery, getModeratorConversationResponse] =
    useConversationLazyQuery({
      fetchPolicy: 'network-only',
    });

  // get conversation
  React.useEffect(() => {
    logger.debug('CHAT', 'GET CONVO', attendee?.id, conversationId);
    if (attendee?.id && attendee?.hasConversation) {
      // attendee
      logger.debug('CHAT', 'GET ATTENDEE CONVO', attendee?.id);

      getAttendeeConversationQuery({
        variables: { attendeeId: attendee?.id },
      });
    } else if (conversationId) {
      logger.debug('CHAT', 'GET MODERATOR CONVO', conversationId);

      // moderator
      getModeratorConversationQuery({
        variables: {
          id: conversationId.toString(),
        },
      });
    }
  }, [
    conversationId,
    attendee?.id,
    attendee?.hasConversation,
    getAttendeeConversationQuery,
    getModeratorConversationQuery,
  ]);

  React.useEffect(() => {
    const queryResult = attendeeId
      ? getAttendeeConversationResponse
      : getModeratorConversationResponse;

    setLoading(queryResult.loading);
    if (queryResult.loading) setCalled(true);
    // set conversation
    const convo = queryResult?.data
      ? attendeeId
        ? (queryResult.data as ConversationForAttendeeQuery).conversationForAttendee
        : (queryResult.data as ConversationQuery).conversation
      : undefined;
    setConversation(convo);
    setConversationQueryResult(queryResult);

    if (convo?.id) {
      logger.debug('CHAT', 'GET CONVO RESULT', convo.id);
    }
  }, [attendeeId, getAttendeeConversationResponse, getModeratorConversationResponse]);

  return {
    called,
    loading,
    conversation,
    conversationQueryResult,
    moderatorConversationQueryResult: getModeratorConversationResponse,
  };
};
